import merge from 'lodash.merge'

import { PaneArea, RequireByKey, ResizeByThumbWithTypeAndDifs, TextAttrs } from '../../../types/shared'
import { PADDING } from '../../constants/common'
import { CanvasElementType } from '../../constants/common'
import math from '../../helpers/math'
import Chart from '../../models/chart'
import PaneModel from '../../models/pane'
import Text from '../text'
import Thumb from '../thumb'
import {
  TailDirection,
  getRectangleSidesCoordinates,
  getTailCoordinates,
  getTailToSideIntersection,
  renderCallout,
} from './callout_utils'
import { IRectangleSidesCoordinates, ITailCoordinates } from './callout_utils'

export interface ICalloutAttrs {
  x1: number
  y1: number
  x2: number
  y2: number
}

interface FontFamilyHeightCoefficient {
  Arial: {
    firstLine: number
    otherLines: number
  }
  Verdana: {
    firstLine: number
    otherLines: number
  }
}

type CalloutTextAttrs = ICalloutAttrs & RequireByKey<TextAttrs, 'text' | 'textAlign' | 'fillStyle' | 'border'>

class Callout<Attrs extends CalloutTextAttrs = CalloutTextAttrs> extends Text<Attrs> {
  static type = CanvasElementType.calloutV1

  name = 'Callout'

  declare fontFamilyHeightCoeficient: FontFamilyHeightCoefficient
  declare isActualBoundingBoxAvailable: boolean
  declare lastAttrs: Attrs
  declare scaled: Pick<Attrs, 'x1' | 'y1' | 'x2' | 'y2'>
  declare measuredHeight: number
  declare requiredTailHalfWidth: number
  declare rectangleSides: IRectangleSidesCoordinates
  declare tail: ITailCoordinates
  declare tailDirection: TailDirection
  declare font: string
  declare context: CanvasRenderingContext2D
  declare lines: {
    text: string
    metrics: TextMetrics
  }[]

  constructor(values: Partial<Attrs>, model: PaneModel) {
    super(values, model)
    this.resize = this.resize.bind(this)
    this._thumbs = [
      new Thumb(
        'tail',
        () => this.attrs.x1,
        () => this.attrs.y1,
        this.resize,
        this.model
      ),
    ]
    this.scale(this.getBoundingPointKeys())
  }

  getBoundingPointKeys = () => ({ x: ['x1', 'x2'], y: ['y1', 'y2'] })

  getModalConfigBase() {
    return {
      inputs: [
        { type: 'multiline_string', name: 'text', required: true },
        { type: 'font', name: 'font' },
        {
          type: 'background',
          name: 'fillStyle',
          label: 'Color',
          default: this.getChartLayoutSettings().ElementSettings.Colors.textWithoutBackground,
        },
        {
          type: 'background',
          name: 'background',
          label: 'Background',
          default: 'rgba(0,0,0,0)',
        },
        {
          type: 'border',
          name: 'border',
          min: 0,
        },
      ],
    }
  }

  getDefaults() {
    const { ElementSettings } = this.getChartLayoutSettings()
    return {
      text: '',
      font: {
        size: 15,
        family: 'Arial',
        style: 'normal',
        weight: 'normal',
      },
      fillStyle: ElementSettings.Colors.textWithoutBackground,
      lineHeight: 15,
      // left | right | center | start | end
      textAlign: 'left',
      // top | middle | alphabetic | bottom
      textBaseline: 'alphabetic',
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      background: ElementSettings.defaultFill,
      border: { color: ElementSettings.Colors.line, width: 1 },
    } as Partial<Attrs>
  }

  setupContext(context: CanvasRenderingContext2D) {
    this.context = context
    context.set('font', this.font)
    context.set('fillStyle', this.attrs.fillStyle)
    context.set('textAlign', this.attrs.textAlign)
    context.set('textBaseline', this.attrs.textBaseline)
    this.setupTextMetricsCache()
  }

  setupTextMetricsCache() {
    if (
      this.attrs.text !== this.lastAttrs?.text ||
      JSON.stringify(this.attrs.font) !== JSON.stringify(this.lastAttrs?.font)
    ) {
      this.lastAttrs = merge({}, this.attrs)
      this.lines = this.getLines().map((line) => ({
        text: line,
        metrics: this.context.measureText(line),
      }))
      this.measuredHeight = this.lines.reduce((acc, current, currentIndex) => {
        const lineHeight =
          current.metrics.actualBoundingBoxAscent +
          current.metrics.actualBoundingBoxDescent +
          (currentIndex !== this.lines.length - 1 ? PADDING.S : 0)
        return acc + lineHeight
      }, 0)
    }
  }

  renderContent(context: CanvasRenderingContext2D) {
    this.render(context)
    if (this.getShouldRenderThumbs()) {
      this.renderThumbs(context)
    }
  }

  renderText() {
    let y = this.scaled.y2 + this.attrs.padding.top
    let previousDescent = 0
    this.lines.forEach((line, index) => {
      y += index === 0 ? 0 : line.metrics.actualBoundingBoxAscent + previousDescent + PADDING.S
      this.context.fillText(line.text, this.scaled.x2 + this.attrs.padding.left, y)
      previousDescent = line.metrics.actualBoundingBoxDescent
    })
  }

  renderBackground() {
    if (!this.attrs.background) {
      return
    }
    const textWidth = this.attrs.text === '' ? this.attrs.font.size * 2 : this.width
    const minRadius = this.attrs.font.size * 0.5
    const padding = this.attrs.border.width / 2 + this.attrs.font.size * 0.5
    const radius = textWidth < minRadius ? textWidth / 2 : minRadius
    this.requiredTailHalfWidth = minRadius
    if (this.measuredHeight < minRadius) {
      this.measuredHeight = radius * 2
    }
    const y2 =
      this.scaled.y2 -
      (this.lines[0].metrics.actualBoundingBoxAscent
        ? this.lines[0].metrics.actualBoundingBoxAscent
        : this.measuredHeight)

    // Points diagram
    //.......X-----------------X.......
    //.................................
    //...X.........................X...
    //...|.........................|...
    //...|.........................|...
    //...X.........................X...
    //.................................
    //.......X-----------------X.......

    this.rectangleSides = getRectangleSidesCoordinates({
      padding,
      radius,
      x2: this.scaled.x2,
      y2,
      textWidth,
      measuredHeight: this.measuredHeight,
    })
    this.tail = getTailCoordinates({ rectangleSides: this.rectangleSides, x1: this.scaled.x1, y1: this.scaled.y1 })
    this.tailDirection = getTailToSideIntersection({ tail: this.tail, rectangleSides: this.rectangleSides })
    this.context.beginPath()
    this.context.set('fillStyle', this.attrs.background)
    this.context.lineJoin = 'round'
    renderCallout({
      context: this.context,
      radius,
      requiredTailHalfWidth: this.requiredTailHalfWidth,
      tail: this.tail,
      rectangleSides: this.rectangleSides,
      tailDirection: this.tailDirection,
    })
    this.context.closePath()
    this.context.fill()
    this.context.set('fillStyle', this.attrs.fillStyle)
    this.renderBorder()
  }

  isInArea(area: PaneArea) {
    if (super.isDrawingElementLockedOrInvisible()) return false
    // is in rectangle
    if (
      this.lines &&
      this.rectangleSides &&
      this.rectangleSides.leftTop.x < area.scaled.x &&
      this.rectangleSides.topLeft.y < area.scaled.y &&
      this.rectangleSides.rightBottom.x > area.scaled.x &&
      this.rectangleSides.bottomRight.y > area.scaled.y
    ) {
      return true
    }
    // is in tail
    if (this.tail) {
      const tailP1 = math.perpendicularPointToLine({
        distance: this.requiredTailHalfWidth,
        line: { x1: this.tail.tailTip.x, y1: this.tail.tailTip.y, x2: this.tail.tailRoot.x, y2: this.tail.tailRoot.y },
      })
      const pointOffsetX = tailP1.x - this.tail.tailRoot.x
      const pointOffsetY = tailP1.y - this.tail.tailRoot.y
      const tailP2 = { x: this.tail.tailRoot.x + -1 * pointOffsetX, y: this.tail.tailRoot.y + -1 * pointOffsetY }
      const polygon = [this.tail.tailTip, tailP1, tailP2]
      if (math.pointInPolygon(area.scaled, polygon)) {
        return true
      }
    }
    return super.isInArea(area)
  }

  moveBy(x: number, y: number) {
    this.attrs.x2 += x
    this.attrs.y2 += y
  }

  resize({ type, difX, difY }: ResizeByThumbWithTypeAndDifs) {
    if (type === 'tail') {
      this.attrs.x1 += difX
      this.attrs.y1 += difY
    }
  }

  getIsInChartView(chart: Chart) {
    if (this.getIsCreator() || !this.lines) {
      return true
    }

    return [...Object.values(this.rectangleSides), this.tail.tailRoot, this.tail.tailTip].some(
      ({ x, y }) => x <= -chart.leftOffset + chart.width && x >= -chart.leftOffset && y >= 0 && y <= this.model.height
    )
  }
}

export default Callout
